import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

/** One assigned task, titles and statuses only (no captured contents). */
export interface OnboardingTaskSummary {
  title: string;
  status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
}

export interface OnboardingPerson {
  id: string;
  full_name: string;
  email: string;
  personal_email: string | null;
  position: string | null;
  contract: string | null;
  start_date: string | null;
  hidden: boolean;
  /**
   * Onboarding finished (person archived). Only ever true when the roster
   * was fetched with include_finished; drives the Finished pill + Reopen.
   */
  finished: boolean;
  /**
   * Attends a dated mobilisation day, so not part of the day-to-day cohort.
   * Hidden from the dashboard by default; revealed by the "On mobilisations"
   * toggle. Reachable in full under that mobilisation's scope.
   */
  on_mobilisation: boolean;
  /** HR paused the automated reminder sweep for this person. */
  reminders_muted: boolean;
  total: number;
  done: number;
  tasks: OnboardingTaskSummary[];
  /** Has begun a form but submitted nothing yet (drives "in progress"). */
  has_started: boolean;
}

export type OnboardingPersonStatus =
  | 'no_tasks'
  | 'not_started'
  | 'in_progress'
  | 'awaiting_approval'
  | 'complete';

/**
 * Single definition of a person's roll-up status so the dashboard
 * filters and the row pill can never disagree. A part-filled but
 * unsubmitted form counts as in progress, not "not started".
 * When every remaining task is sat in awaiting_review (e.g. DBS
 * waiting on HR sign-off) the ball is with us, not the inductee,
 * so that surfaces as awaiting_approval rather than in_progress.
 */
export function onboardingPersonStatus(p: OnboardingPerson): OnboardingPersonStatus {
  if (p.total === 0) return 'no_tasks';
  if (p.done >= p.total) return 'complete';
  const remaining = p.tasks.filter((t) => t.status !== 'complete');
  if (remaining.length > 0 && remaining.every((t) => t.status === 'awaiting_review')) {
    return 'awaiting_approval';
  }
  if (p.done > 0 || p.has_started) return 'in_progress';
  return 'not_started';
}

export const PEOPLE_KEY = ['onboarding-people'] as const;

/**
 * The onboarding roster. Pass includeFinished to also return archived
 * ("finished") people so the dashboard can show them behind the "Show
 * finished" toggle. The base PEOPLE_KEY stays a prefix of the query key so
 * existing invalidateQueries({ queryKey: PEOPLE_KEY }) still refreshes both.
 */
export function useOnboardingPeople(includeFinished = false) {
  return useQuery({
    queryKey: [...PEOPLE_KEY, { finished: includeFinished }],
    queryFn: async () =>
      (
        await api<{ people: OnboardingPerson[] }>(
          `/api/admin/onboarding/people${includeFinished ? '?include_finished=1' : ''}`,
        )
      ).people,
  });
}

/**
 * Finish (archive) or reopen (unarchive) a person's onboarding. Reuses the
 * audited PersonArchived / PersonUnarchived events; finishing drops them off
 * the active dashboard, reopening brings them back. Reversible either way.
 */
export function useFinishOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      personId,
      finished,
      reason,
    }: {
      personId: string;
      finished: boolean;
      reason?: string;
    }) =>
      api<{ event: unknown }>(
        `/api/admin/people/${personId}/${finished ? 'archive' : 'unarchive'}`,
        { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
      ),
    onSuccess: (_data, { personId }) => {
      qc.invalidateQueries({ queryKey: PEOPLE_KEY });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}

/** Tick people on the dashboard and assign content / a template in bulk. */
export interface BulkAssignInput {
  person_ids: string[];
  items?: { type: 'document' | 'form' | 'task' | 'training_module'; id: string }[];
  apply_template?: boolean;
}

export function useBulkAssign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkAssignInput) =>
      api<{ people: number; assigned: number }>('/api/admin/onboarding/bulk-assign', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PEOPLE_KEY }),
  });
}

/** Hide / show a person on the onboarding dashboard (not-a-starter flag). */
export function useSetOnboardingVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personId, hidden }: { personId: string; hidden: boolean }) =>
      api<{ hidden: boolean }>(`/api/admin/onboarding/people/${personId}/visibility`, {
        method: 'POST',
        body: JSON.stringify({ hidden }),
      }),
    onSuccess: (_data, { personId }) => {
      qc.invalidateQueries({ queryKey: PEOPLE_KEY });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}

/**
 * Pause / resume the automated reminder sweep for a person. The manual
 * Send Reminder button and the new-task digest still send while muted.
 */
export function useSetRemindersMuted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personId, muted }: { personId: string; muted: boolean }) =>
      api<{ muted: boolean }>(`/api/admin/onboarding/people/${personId}/reminders-mute`, {
        method: 'POST',
        body: JSON.stringify({ muted }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PEOPLE_KEY }),
  });
}

/** Apply the person's role template on demand (idempotent). */
export function useApplyTemplate(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ assigned: number; skipped?: string }>(
        `/api/admin/onboarding/people/${personId}/apply-template`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-documents', personId] });
      qc.invalidateQueries({ queryKey: ['person-forms', personId] });
      qc.invalidateQueries({ queryKey: ['person-catalog-tasks', personId] });
      qc.invalidateQueries({ queryKey: ['person-training-modules', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}
